import type { ProviderFeeConfigItem, WithdrawalRequest } from '../types';

const FALLBACK_METHOD_ID = 'all';

function roundMoney2(value: number): number {
  return Math.round(value * 100) / 100;
}

function clampRate(rate: number): number {
  const n = Number(rate);
  if (!Number.isFinite(n) || n < 0) return 0;
  if (n >= 1) return 0;
  return n;
}

function clampMoney(value: number): number {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return n;
}

function normalizeNetworkFeeType(raw: unknown): 'none' | 'percent' | 'fixed' {
  const type = String(raw || 'none')
    .trim()
    .toLowerCase();
  if (type === 'percent' || type === 'fixed') return type;
  return 'none';
}

export function getWithdrawalMetadata(withdrawal: WithdrawalRequest): Record<string, unknown> {
  return withdrawal.metadata && typeof withdrawal.metadata === 'object'
    ? (withdrawal.metadata as Record<string, unknown>)
    : {};
}

export function isPortalUsdWithdrawal(withdrawal: WithdrawalRequest): boolean {
  const meta = getWithdrawalMetadata(withdrawal);
  return (
    withdrawal.provider === 'portal_usd_withdrawal' ||
    (typeof withdrawal.transactionId === 'string' && withdrawal.transactionId.startsWith('PORTAL_USD_WD_')) ||
    meta.portalUsdWithdrawal === true
  );
}

export function getRequestedNetUsd(withdrawal: WithdrawalRequest): number {
  const meta = getWithdrawalMetadata(withdrawal);
  const fromMeta = Number(meta.requestedNetUsd);
  if (Number.isFinite(fromMeta) && fromMeta > 0) {
    return roundMoney2(fromMeta);
  }
  return roundMoney2(withdrawal.amount);
}

export function getTierFundingUsd(withdrawal: WithdrawalRequest): number | null {
  const meta = getWithdrawalMetadata(withdrawal);
  const row = withdrawal as WithdrawalRequest & { tierFundingUsd?: number | null };
  const candidates = [row.tierFundingUsd, meta.tierFundingUsd, meta.computedGrossDebitUsd];

  for (const candidate of candidates) {
    const value = Number(candidate);
    if (Number.isFinite(value) && value > 0) {
      return roundMoney2(value);
    }
  }

  return null;
}

export function getWithdrawalDisplayAmount(withdrawal: WithdrawalRequest): number {
  if (!isPortalUsdWithdrawal(withdrawal)) {
    return withdrawal.amount;
  }
  return getTierFundingUsd(withdrawal) ?? withdrawal.amount;
}

export function formatWithdrawalDisplayAmount(withdrawal: WithdrawalRequest, options?: { signed?: boolean }): string {
  const amount = getWithdrawalDisplayAmount(withdrawal);
  const sign = options?.signed ? '-' : '';
  return `${sign}$${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function computeNetworkFeeAmount(
  baseUsd: number,
  rates: {
    networkFeeType?: unknown;
    networkFeeRate?: number;
    networkFeeFixedUsd?: number;
  }
): number {
  const base = roundMoney2(baseUsd);
  const networkFeeType = normalizeNetworkFeeType(rates.networkFeeType);
  if (networkFeeType === 'percent') {
    return roundMoney2(base * clampRate(rates.networkFeeRate ?? 0));
  }
  if (networkFeeType === 'fixed') {
    return roundMoney2(clampMoney(rates.networkFeeFixedUsd ?? 0));
  }
  return 0;
}

function computeGatewayFeeAmount(
  netUsd: number,
  gatewayFeeRate: number,
  paymentGateFeeMinUsd = 0
): number {
  const net = roundMoney2(netUsd);
  const rate = clampRate(gatewayFeeRate);
  const percentAmount = roundMoney2(net * rate);
  const minUsd = clampMoney(paymentGateFeeMinUsd);
  if (minUsd > 0) {
    return roundMoney2(Math.max(percentAmount, minUsd));
  }
  return percentAmount;
}

export type NetWithdrawalFeeSnapshot = {
  netPayoutUsd: number;
  systemFeeRate: number;
  systemFeeAmount: number;
  gatewayFeeRate: number;
  gatewayFeeAmount: number;
  networkFeeType: 'none' | 'percent' | 'fixed';
  networkFeeRate: number;
  networkFeeAmount: number;
  gatewayPayoutUsd: number;
  tierFundingUsd: number;
};

/** Mirrors backend `computeNetWithdrawalFeeSnapshot` (NET receive model). */
export function computeNetWithdrawalFeeSnapshot(
  netUsd: number,
  rates: {
    systemFeeRate?: number;
    gatewayFeeRate?: number;
    paymentGateFeeMinUsd?: number;
    networkFeeType?: unknown;
    networkFeeRate?: number;
    networkFeeFixedUsd?: number;
  } = {}
): NetWithdrawalFeeSnapshot {
  const net = roundMoney2(netUsd);
  const systemFeeRate = clampRate(rates.systemFeeRate ?? 0);
  const gatewayFeeRate = clampRate(rates.gatewayFeeRate ?? 0);
  const paymentGateFeeMinUsd = clampMoney(rates.paymentGateFeeMinUsd ?? 0);
  const networkFeeType = normalizeNetworkFeeType(rates.networkFeeType);
  const networkFeeRate = clampRate(rates.networkFeeRate ?? 0);
  const systemFeeAmount = roundMoney2(net * systemFeeRate);
  const gatewayFeeAmount = computeGatewayFeeAmount(net, gatewayFeeRate, paymentGateFeeMinUsd);
  const networkFeeAmount = computeNetworkFeeAmount(net, rates);
  const tierFundingUsd = roundMoney2(net + systemFeeAmount + gatewayFeeAmount + networkFeeAmount);
  const gatewayPayoutUsd = tierFundingUsd;

  return {
    netPayoutUsd: net,
    systemFeeRate,
    systemFeeAmount,
    gatewayFeeRate,
    gatewayFeeAmount,
    networkFeeType,
    networkFeeRate: networkFeeType === 'percent' ? networkFeeRate : 0,
    networkFeeAmount,
    gatewayPayoutUsd,
    tierFundingUsd,
  };
}

export function findOnlineDebtInternalWithdrawalFee(
  items: ProviderFeeConfigItem[]
): ProviderFeeConfigItem | null {
  return findPortalWithdrawalFeeConfigByProvider(items, 'online_debt', 'internal');
}

function normalizeKey(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

function resolveProviderMethodFromPayoutNetwork(payoutNetworkRaw: unknown): { provider: string; methodId: string } | null {
  const payoutNetwork = normalizeKey(payoutNetworkRaw);
  if (payoutNetwork === 'btc') {
    return { provider: 'btcpay', methodId: 'bitcoin_transfer' };
  }
  if (payoutNetwork === 'eth') {
    return { provider: 'zeroxprocessing', methodId: 'zerox_eth' };
  }
  if (payoutNetwork === 'usdt_erc') {
    return { provider: 'zeroxprocessing', methodId: 'zerox_usdt_erc20' };
  }
  if (payoutNetwork === 'usdt_trc') {
    return { provider: 'zeroxprocessing', methodId: 'zerox_usdt_trc20' };
  }
  return null;
}

export function findPortalWithdrawalFeeConfigByProvider(
  items: ProviderFeeConfigItem[],
  providerRaw: unknown,
  methodIdRaw: unknown
): ProviderFeeConfigItem | null {
  const provider = normalizeKey(providerRaw);
  const methodId = normalizeKey(methodIdRaw) || FALLBACK_METHOD_ID;
  if (!provider) return null;

  const exact = items.find(
    (item) =>
      normalizeKey(item.provider) === provider &&
      normalizeKey(item.operation) === 'withdrawal' &&
      normalizeKey(item.methodId || FALLBACK_METHOD_ID) === methodId
  );
  if (exact) return exact;

  return (
    items.find(
      (item) =>
        normalizeKey(item.provider) === provider &&
        normalizeKey(item.operation) === 'withdrawal' &&
        normalizeKey(item.methodId || FALLBACK_METHOD_ID) === FALLBACK_METHOD_ID
    ) ?? null
  );
}

export function findPortalWithdrawalFeeConfig(
  withdrawal: WithdrawalRequest,
  items: ProviderFeeConfigItem[]
): ProviderFeeConfigItem | null {
  const meta = getWithdrawalMetadata(withdrawal);
  const fromMetadata = {
    provider: normalizeKey(meta.feeProvider),
    methodId: normalizeKey(meta.feeMethodId || meta.matchedFeeMethodId || meta.withdrawalType),
  };

  if (fromMetadata.provider) {
    const found = findPortalWithdrawalFeeConfigByProvider(items, fromMetadata.provider, fromMetadata.methodId);
    if (found) return found;
  }

  const fromNetwork = resolveProviderMethodFromPayoutNetwork(meta.payoutNetwork);
  if (fromNetwork) {
    return findPortalWithdrawalFeeConfigByProvider(items, fromNetwork.provider, fromNetwork.methodId);
  }

  return findPortalWithdrawalFeeConfigByProvider(items, withdrawal.provider, withdrawal.withdrawalType);
}

function ratesFromProviderFeeConfig(item: ProviderFeeConfigItem) {
  return {
    systemFeeRate: item.systemFeeRate ?? 0,
    gatewayFeeRate: item.paymentGateFeeRate ?? 0,
    // Payment gate min fee is no longer part of Fee Configuration.
    // Ignore legacy DB values so breakdown uses gateway percentage only.
    paymentGateFeeMinUsd: 0,
    networkFeeType: item.networkFeeType ?? 'none',
    networkFeeRate: item.networkFeeRate ?? 0,
    networkFeeFixedUsd: item.networkFeeAmount ?? 0,
  };
}

export type PortalUsdCurrentFeeReference = {
  totalDebitedUsd: number;
  systemFeeAmount: number;
  gatewayFeeAmount: number;
  networkFeeAmount: number;
  expectedNetReceiveUsd: number;
};

/** Current provider/method fee config used to explain this portal USD withdrawal. */
export function computePortalUsdCurrentFeeReference(
  totalDebitedUsd: number,
  requestedNetUsd: number,
  configRow: ProviderFeeConfigItem
): PortalUsdCurrentFeeReference {
  const totalDebited = roundMoney2(totalDebitedUsd);
  void requestedNetUsd;
  const rates = ratesFromProviderFeeConfig(configRow);
  const systemFeeRate = clampRate(rates.systemFeeRate ?? 0);
  const gatewayFeeRate = clampRate(rates.gatewayFeeRate ?? 0);
  const networkFeeType = normalizeNetworkFeeType(rates.networkFeeType);
  const networkFeeRate = networkFeeType === 'percent' ? clampRate(rates.networkFeeRate ?? 0) : 0;
  const fixedNetworkFee = networkFeeType === 'fixed' ? clampMoney(rates.networkFeeFixedUsd ?? 0) : 0;
  const divisor = 1 + systemFeeRate + gatewayFeeRate + networkFeeRate;
  const rawNet = divisor > 0 ? Math.max(0, (totalDebited - fixedNetworkFee) / divisor) : totalDebited;
  const expectedNetReceiveUsd = roundMoney2(rawNet);
  const systemFeeAmount = roundMoney2(rawNet * systemFeeRate);
  const gatewayFeeAmount = roundMoney2(rawNet * gatewayFeeRate);
  const networkFeeAmount =
    networkFeeType === 'fixed' ? roundMoney2(fixedNetworkFee) : roundMoney2(rawNet * networkFeeRate);

  return {
    totalDebitedUsd: totalDebited,
    systemFeeAmount,
    gatewayFeeAmount,
    networkFeeAmount,
    expectedNetReceiveUsd,
  };
}

function buildConfiguredProviderFee(item: ProviderFeeConfigItem) {
  return {
    source: 'current_provider_fee_config',
    provider: item.provider,
    operation: item.operation,
    methodId: item.methodId ?? FALLBACK_METHOD_ID,
    matchedMethodId: item.methodId ?? FALLBACK_METHOD_ID,
    paymentGateFeeRate: item.paymentGateFeeRate,
    paymentGateFeeMinUsd: item.paymentGateFeeMinUsd,
    systemFeeRate: item.systemFeeRate,
    feeRate: item.feeRate,
    networkFeeType: item.networkFeeType,
    networkFeeRate: item.networkFeeRate,
    networkFeeAmount: item.networkFeeAmount,
    isEnabled: item.isEnabled,
  };
}

function readTxnFeeSnapshot(withdrawal: WithdrawalRequest, requestedNetUsd: number) {
  const meta = getWithdrawalMetadata(withdrawal);
  const systemFeeAmount = Number(meta.systemFeeAmount ?? 0);
  const gatewayFeeAmount = Number(meta.gatewayFeeAmount ?? 0);
  const networkFeeAmount = Number(meta.networkFeeAmount ?? 0);
  const tierFundingUsd = getTierFundingUsd(withdrawal);

  return {
    feeModel: 'net_receive' as const,
    netPayoutUsd: requestedNetUsd,
    systemFeeAmount: Number.isFinite(systemFeeAmount) ? roundMoney2(systemFeeAmount) : null,
    gatewayFeeAmount: Number.isFinite(gatewayFeeAmount) ? roundMoney2(gatewayFeeAmount) : null,
    networkFeeType: (meta.networkFeeType as string | undefined) ?? 'none',
    networkFeeAmount: Number.isFinite(networkFeeAmount) ? roundMoney2(networkFeeAmount) : null,
    gatewayPayoutUsd: roundMoney2(
      requestedNetUsd +
        (Number.isFinite(systemFeeAmount) ? systemFeeAmount : 0) +
        (Number.isFinite(gatewayFeeAmount) ? gatewayFeeAmount : 0) +
        (Number.isFinite(networkFeeAmount) ? networkFeeAmount : 0)
    ),
    tierFundingUsd:
      tierFundingUsd ??
      roundMoney2(
        requestedNetUsd +
          (Number.isFinite(systemFeeAmount) ? systemFeeAmount : 0) +
          (Number.isFinite(gatewayFeeAmount) ? gatewayFeeAmount : 0) +
          (Number.isFinite(networkFeeAmount) ? networkFeeAmount : 0)
      ),
    source:
      networkFeeAmount > 0 || systemFeeAmount > 0
        ? 'transaction_snapshot'
        : 'transaction_snapshot_missing_network_fee',
  };
}

export function enrichPortalUsdWithdrawal(
  withdrawal: WithdrawalRequest,
  providerFeeItems: ProviderFeeConfigItem[] = []
): WithdrawalRequest {
  if (!isPortalUsdWithdrawal(withdrawal)) {
    return withdrawal;
  }

  const requestedNetUsd = getRequestedNetUsd(withdrawal);
  const feeSnapshot = readTxnFeeSnapshot(withdrawal, requestedNetUsd);
  const configRow = findPortalWithdrawalFeeConfig(withdrawal, providerFeeItems);

  if (!configRow) {
    return {
      ...withdrawal,
      feeSnapshot,
    };
  }

  const totalDebitedUsd = feeSnapshot.tierFundingUsd ?? getTierFundingUsd(withdrawal) ?? requestedNetUsd;
  const currentReference = computePortalUsdCurrentFeeReference(
    totalDebitedUsd,
    requestedNetUsd,
    configRow
  );
  const configuredProviderFee = buildConfiguredProviderFee(configRow);

  return {
    ...withdrawal,
    feeSnapshot: {
      ...feeSnapshot,
      tierFundingUsd: currentReference.totalDebitedUsd,
      systemFeeRate: configRow.systemFeeRate ?? 0,
      systemFeeAmount: currentReference.systemFeeAmount,
      gatewayFeeRate: configRow.paymentGateFeeRate ?? 0,
      gatewayFeeAmount: currentReference.gatewayFeeAmount,
      networkFeeType: configRow.networkFeeType ?? 'none',
      networkFeeRate: configRow.networkFeeRate ?? 0,
      networkFeeAmount: currentReference.networkFeeAmount,
    },
    configuredProviderFee,
    configuredPortalFee: configuredProviderFee,
    expectedPayoutBreakdown: {
      expectedNetworkFeeUsd: currentReference.networkFeeAmount,
      expectedNetworkFeeSource: 'current_provider_fee_config',
      expectedReceivedUsd: currentReference.expectedNetReceiveUsd,
      isEstimate: true,
    },
  };
}

export function enrichWithdrawalsWithPortalUsdFees(
  withdrawals: WithdrawalRequest[],
  providerFeeItems: ProviderFeeConfigItem[] = []
): WithdrawalRequest[] {
  return withdrawals.map((withdrawal) => enrichPortalUsdWithdrawal(withdrawal, providerFeeItems));
}
